import React, { useState, useEffect } from 'react';
import SharedCard from './SharedCard';
import { downloadReceiptAsImage } from '../utils/receiptToImage';

const mxn = n => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const buildReceiptHTML = ({ folio, fecha, studentName, items, subtotal, total, discountTotal, academyName }) => {
  const itemsRows = items.map(it => {
    const line = Number(it.price) * it.quantity;
    const disc = it.discount > 0 ? Math.min(line, Number(it.discount)) : 0;
    const net  = Math.max(0, line - disc);
    const meta = [it.color ? `Color: ${it.color}` : '', it.size ? `Talla: ${it.size}` : ''].filter(Boolean).join(' · ');
    return `<tr><td><div class="prod-name">${it.name}</div>${meta ? `<div class="prod-meta">${meta}</div>` : ''}</td><td class="right">${it.quantity}</td><td class="right">${mxn(it.price)}</td><td class="right" style="font-weight:600">${mxn(net)}</td></tr>`;
  }).join('');

  return `<!doctype html><html lang="es"><head><meta charset="utf-8"/><title>Comprobante ${folio}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Inter',system-ui,sans-serif;background:#f3f4f6;display:flex;justify-content:center;padding:40px 16px}
  .card{background:white;width:100%;max-width:580px;border-radius:20px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,.12)}
  .header{background:linear-gradient(135deg,#961914,#c0392b);padding:32px;color:white}
  .header-top{display:flex;justify-content:space-between;align-items:flex-start}
  .brand h1{font-size:20px;font-weight:700}.brand p{font-size:12px;opacity:.75;margin-top:3px}
  .folio{background:rgba(255,255,255,.15);border-radius:10px;padding:8px 14px;text-align:right}
  .folio .num{font-size:18px;font-weight:700}.folio .lbl{font-size:11px;opacity:.7}
  .client{margin-top:20px;padding-top:20px;border-top:1px solid rgba(255,255,255,.2)}
  .client .lbl{font-size:11px;opacity:.7;text-transform:uppercase;letter-spacing:.5px}
  .client .name{font-size:18px;font-weight:600;margin-top:4px}
  .body{padding:24px 32px}
  .section-title{font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.5px;color:#9ca3af;margin-bottom:12px}
  table{width:100%;border-collapse:collapse}
  th{text-align:left;font-size:11px;font-weight:600;color:#6b7280;text-transform:uppercase;padding:0 0 8px;border-bottom:2px solid #f3f4f6}
  th.right,td.right{text-align:right}
  td{padding:10px 0;border-bottom:1px solid #f9fafb;vertical-align:top;font-size:13px}
  .prod-name{font-weight:600;color:#111827}.prod-meta{font-size:11px;color:#9ca3af;margin-top:2px}
  .totals{margin-top:16px;background:#f9fafb;border-radius:12px;padding:16px}
  .total-row{display:flex;justify-content:space-between;font-size:13px;color:#6b7280;padding:4px 0}
  .total-final{display:flex;justify-content:space-between;align-items:center;margin-top:12px;padding-top:12px;border-top:2px solid #e5e7eb}
  .total-final .lbl{font-size:15px;font-weight:700;color:#111827}
  .total-final .val{font-size:22px;font-weight:800;color:#961914}
  .footer{padding:20px 32px;background:#fafafa;border-top:1px solid #f3f4f6}
  .footer p{font-size:11px;color:#9ca3af;line-height:1.7}
</style></head><body>
<div class="card">
  <div class="header">
    <div class="header-top">
      <div class="brand"><h1>${academyName || 'Punto de Venta'}</h1><p>Comprobante de venta</p></div>
      <div class="folio"><div class="lbl">Folio</div><div class="num">${folio}</div></div>
    </div>
    <div class="client"><div class="lbl">Cliente</div><div class="name">${studentName}</div><div style="font-size:12px;opacity:.7;margin-top:6px">${fecha}</div></div>
  </div>
  <div class="body">
    <div class="section-title">Detalle de productos</div>
    <table><thead><tr><th>Producto</th><th class="right">Cant.</th><th class="right">Precio</th><th class="right">Importe</th></tr></thead><tbody>${itemsRows}</tbody></table>
    <div class="totals">
      <div class="total-row"><span>Subtotal</span><span>${mxn(subtotal)}</span></div>
      ${discountTotal > 0 ? `<div class="total-row"><span>Descuentos</span><span style="color:#961914">-${mxn(discountTotal)}</span></div>` : ''}
      <div class="total-final"><span class="lbl">Total</span><span class="val">${mxn(total)}</span></div>
    </div>
  </div>
  <div class="footer"><p>Este comprobante es un documento informativo. Para factura fiscal, solicítela en recepción.</p></div>
</div>
</body></html>`;
};

const SalesPage = ({ currentUser }) => {
  const [sales,     setSales]     = useState([]);
  const [students,  setStudents]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate,   setEndDate]   = useState('');
  const [search,    setSearch]    = useState('');
  const [msg,       setMsg]       = useState(null);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  const loadData = async () => {
    try {
      const [salRes, stuRes] = await Promise.all([fetch('/api/list_sales.php', { credentials: 'include' }), fetch('/api/list_students.php', { credentials: 'include' })]);
      const [salData, stuData] = await Promise.all([salRes.json(), stuRes.json()]);
      setSales(Array.isArray(salData) ? salData : []);
      setStudents(Array.isArray(stuData) ? stuData : []);
    } catch { setSales([]); setStudents([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const getStudent      = id => students.find(x => String(x.id) === String(id));
  const getStudentName  = id => { const s = getStudent(id); return s?.name ?? s?.studentName ?? '—'; };
  const getStudentPhone = id => getStudent(id)?.phone || null;

  const parseDate = str => {
    if (!str) return null;
    const parts = str.split('/');
    if (parts.length === 3) { const [d, m, y] = parts; return new Date(`${y}-${m}-${d}T00:00:00`).getTime(); }
    return new Date(str).getTime();
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar esta venta? Esta acción no se puede deshacer.')) return;
    try {
      const res  = await fetch('/api/delete_sale.php', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id }) });
      const data = await res.json();
      if (data.success) { showMsg('success', 'Venta eliminada.'); loadData(); }
      else showMsg('error', data.message || 'Error al eliminar.');
    } catch { showMsg('error', 'Error de conexión.'); }
  };

  const handlePrint = sale => {
    const items         = Array.isArray(sale.products) ? sale.products : [];
    const subtotal      = items.reduce((s, p) => s + Number(p.price) * p.quantity, 0);
    const discountTotal = items.reduce((s, p) => s + Math.max(0, Number(p.discount || 0)), 0);
    const folio         = `POS-${String(sale.id).padStart(6, '0')}`;
    const studentName   = sale.studentName || getStudentName(sale.studentId);
    const phone         = sale.studentPhone || getStudentPhone(sale.studentId);
    const html = buildReceiptHTML({ folio, fecha: sale.date || '', studentName, items, subtotal, total: Number(sale.total), discountTotal, academyName: currentUser?.academy_name || '' });
    // FIX: descarga PNG + abre WhatsApp si hay teléfono
    downloadReceiptAsImage(html, folio, phone ? { phone, studentName, type: 'venta' } : null);
  };

  const filtered     = sales.filter(sale => {
    const ts = parseDate(sale.date);
    if (startDate && ts < new Date(startDate + 'T00:00:00').getTime()) return false;
    if (endDate   && ts > new Date(endDate   + 'T23:59:59').getTime()) return false;
    if (search && !getStudentName(sale.studentId).toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const totalGeneral = filtered.reduce((s, sale) => s + Number(sale.total || 0), 0);

  if (loading) return <SharedCard title="Ventas"><div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-[#961914] border-t-transparent rounded-full animate-spin" /></div></SharedCard>;

  return (
    <SharedCard title="Ventas">
      {msg && <div className={`mb-4 p-3 rounded-lg text-sm font-medium ${msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>{msg.text}</div>}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-[#961914] to-red-700 p-4 rounded-xl text-white text-center"><p className="text-xs opacity-75 uppercase tracking-wide">Total período</p><p className="text-2xl font-extrabold mt-1">{mxn(totalGeneral)}</p></div>
        <div className="bg-gradient-to-br from-gray-700 to-gray-900 p-4 rounded-xl text-white text-center"><p className="text-xs opacity-75 uppercase tracking-wide">Transacciones</p><p className="text-2xl font-extrabold mt-1">{filtered.length}</p></div>
        <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-4 rounded-xl text-white text-center md:col-span-1 col-span-2"><p className="text-xs opacity-75 uppercase tracking-wide">Promedio por venta</p><p className="text-2xl font-extrabold mt-1">{mxn(filtered.length > 0 ? totalGeneral / filtered.length : 0)}</p></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div><label className="block text-xs font-semibold text-gray-600 mb-1">Fecha inicio</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" /></div>
        <div><label className="block text-xs font-semibold text-gray-600 mb-1">Fecha fin</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" /></div>
        <div><label className="block text-xs font-semibold text-gray-600 mb-1">Buscar cliente</label><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Nombre del estudiante…" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#961914]/30" /></div>
        <div className="flex items-end"><button onClick={() => { setStartDate(''); setEndDate(''); setSearch(''); }} className="w-full px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium hover:bg-gray-200 transition">Limpiar filtros</button></div>
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400"><div className="text-4xl mb-2">🛒</div><p className="text-sm">No hay ventas en este período.</p></div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
          <table className="min-w-full bg-white">
            <thead><tr className="bg-[#961914] text-white text-xs uppercase tracking-wider"><th className="py-3 px-4 text-left">Folio</th><th className="py-3 px-4 text-left">Cliente</th><th className="py-3 px-4 text-left">Productos</th><th className="py-3 px-4 text-right">Total</th><th className="py-3 px-4 text-left">Fecha</th><th className="py-3 px-4 text-center">Acciones</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(sale => {
                const items = Array.isArray(sale.products) ? sale.products : [];
                return (
                  <tr key={sale.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4 text-xs text-gray-400 font-mono">POS-{String(sale.id).padStart(6, '0')}</td>
                    <td className="py-3 px-4"><p className="text-sm font-semibold text-gray-900 whitespace-nowrap">{sale.studentName || getStudentName(sale.studentId)}</p></td>
                    <td className="py-3 px-4"><div className="flex flex-wrap gap-1">{items.length === 0 ? <span className="text-gray-300 text-xs">—</span> : items.map((p, i) => <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">{p.name}<span className="text-gray-400">×{p.quantity}</span>{p.discountPercent > 0 && <span className="text-[#961914]">-{p.discountPercent}%</span>}</span>)}</div></td>
                    <td className="py-3 px-4 text-right font-bold text-gray-900 whitespace-nowrap">{mxn(sale.total)}</td>
                    <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{sale.date}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex justify-center gap-1">
                        <button onClick={() => handlePrint(sale)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors">📸 Comprobante</button>
                        <button onClick={() => handleDelete(sale.id)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors">Eliminar</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr className="bg-gray-50 border-t-2 border-gray-200"><td colSpan="3" className="px-4 py-3 text-sm font-semibold text-right text-gray-600">Total ({filtered.length} ventas):</td><td className="px-4 py-3 text-right font-extrabold text-[#961914] whitespace-nowrap">{mxn(totalGeneral)}</td><td colSpan="2" /></tr></tfoot>
          </table>
        </div>
      )}
    </SharedCard>
  );
};

export default SalesPage;
